// Session-only cache. Nothing is written to localStorage or shared between users.
const entries = new Map();
let sessionVersion = 0;
const listeners = new Map();
const EMPTY = Object.freeze({ data: undefined, error: null, updatedAt: 0, fetching: false });
const MAX_ENTRIES = 60;
const RETENTION = 5 * 60 * 1000;
const stable = value => JSON.stringify(value, (_, item) => item && typeof item === 'object' && !Array.isArray(item)
  ? Object.keys(item).sort().reduce((result, key) => { result[key] = item[key]; return result; }, {}) : item);
const notify = key => listeners.get(key)?.forEach(listener => listener());

// Preserve unchanged objects, including QR artwork, across background refreshes.
export function shareData(previous, next) {
  if (previous === next) return previous;
  if (!previous || !next || typeof previous !== 'object' || typeof next !== 'object' || Array.isArray(previous) !== Array.isArray(next)) return next;
  const keys = Object.keys(next), result = Array.isArray(next) ? [] : {};
  let equal = Object.keys(previous).length === keys.length;
  keys.forEach(key => { result[key] = shareData(previous[key], next[key]); if (result[key] !== previous[key]) equal = false; });
  return equal ? previous : result;
}
function prune() {
  const now = Date.now();
  for (const [key, entry] of entries) {
    if (!listeners.get(key)?.size && !entry.promise && (now - entry.touched > RETENTION || entries.size > MAX_ENTRIES)) entries.delete(key);
  }
}
export function querySnapshot(key) { return entries.get(key)?.snapshot || EMPTY; }
export function subscribeQuery(key, listener) {
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(listener);
  return () => { listeners.get(key)?.delete(listener); if (!listeners.get(key)?.size) listeners.delete(key); };
}
export function clearQueryCache() {
  sessionVersion++;
  entries.clear(); // Detached in-flight requests cannot repopulate the new session.
  listeners.forEach((_, key) => notify(key));
}
export function fetchQuery(query, { force = false } = {}) {
  prune();
  let entry = entries.get(query.key);
  if (entry?.promise) return entry.promise;
  if (!force && entry?.snapshot.data !== undefined && Date.now() - entry.snapshot.updatedAt < query.ttl) {
    entry.touched = Date.now();
    return Promise.resolve(entry.snapshot.data);
  }
  entry = { query, sessionVersion, touched: Date.now(), snapshot: { ...(entry?.snapshot || EMPTY), error: null, fetching: true } };
  entries.set(query.key, entry);
  entry.promise = Promise.resolve().then(() => query.load({ force })).then(data => {
    if (entries.get(query.key) !== entry) {
      if (entry.sessionVersion !== sessionVersion) throw new Error('Sessão encerrada.');
      // A mutation superseded this GET. Imperative callers also receive the current
      // result, instead of resurrecting deleted items with an older response.
      return fetchQuery(query);
    }
    if (entries.get(query.key) === entry) {
      entry.snapshot = { data: shareData(entry.snapshot.data, data), error: null, updatedAt: Date.now(), fetching: false };
    }
    return data;
  }, error => {
    if (entries.get(query.key) === entry) entry.snapshot = { ...entry.snapshot, error, fetching: false };
    throw error;
  }).finally(() => {
    if (entries.get(query.key) === entry) { entry.promise = null; notify(query.key); prune(); }
  });
  notify(query.key);
  return entry.promise;
}
export function defineQuery(scope, load, { ttl = 30000, normalize = value => value ?? {} } = {}) {
  const describe = params => {
    const value = normalize(params);
    return { key: `${scope}:${stable(value)}`, scope, params: value, ttl, load: options => load(value, options) };
  };
  const read = (params, options) => fetchQuery(describe(params), options);
  read.describe = describe;
  read.peek = params => querySnapshot(describe(params).key).data;
  return read;
}
export function invalidateQueries(scope, { discard = false, match = () => true } = {}) {
  for (const [key, entry] of [...entries]) {
    if (entry.query.scope !== scope || !match(entry.query.params)) continue;
    const next = { ...entry, promise: null, snapshot: { ...(discard ? EMPTY : entry.snapshot), error: null, updatedAt: 0, fetching: false } };
    entries.set(key, next);
    notify(key);
    if (listeners.get(key)?.size) fetchQuery(next.query, { force: true }).catch(() => {});
  }
}
export function patchQueries(scope, patch) {
  for (const [key, entry] of [...entries]) {
    if (entry.query.scope !== scope) continue;
    if (entry.snapshot.data === undefined) {
      entries.delete(key);
      if (listeners.get(key)?.size) fetchQuery(entry.query).catch(() => {});
      continue;
    }
    // Replace the entry as well as its value so an older GET cannot undo a save.
    entries.set(key, { ...entry, promise: null, snapshot: { ...entry.snapshot, data: shareData(entry.snapshot.data, patch(entry.snapshot.data, entry.query.params)), error: null, fetching: false } });
    notify(key);
  }
}